import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTriggerElement = SelectPrimitive.Trigger;
const SelectIcon = SelectPrimitive.Icon;
const SelectContentElement = SelectPrimitive.Content;
const SelectItemElement = SelectPrimitive.Item;
const SelectViewport = SelectPrimitive.Viewport;
const SelectItemText = SelectPrimitive.ItemText;
const SelectItemIndicator = SelectPrimitive.ItemIndicator;
const SelectScrollUpButtonElement = SelectPrimitive.ScrollUpButton;
const SelectScrollDownButtonElement = SelectPrimitive.ScrollDownButton;

interface SelectOption {
  value: string;
  label: string;
  /** Renders this option bolder than the rest, e.g. an action item mixed into a list of data. */
  emphasized?: boolean;
  /** When set, a section header is rendered above this option in the dropdown. Items without a header continue the previous section. */
  groupHeader?: string;
}

interface SelectProps {
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  value?: string;
  textSize?: string;
  disabled?: boolean;
  ariaLabel?: string;
}
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      "bg-surface-card border-surface-border text-text-primary data-[placeholder]:text-muted focus:ring-primary ring-offset-background",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
        "border-surface-border bg-surface-card text-text-primary",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "focus:bg-surface-hover focus:text-text-primary",
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(
      "-mx-1 my-1 h-px bg-surface-border",
      className
    )}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export const RadixSelect: React.FC<SelectProps> = ({
  options,
  onChange,
  placeholder,
  value,
  textSize = "text-sm",
  disabled,
  ariaLabel,
}) => {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTriggerElement
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          "inline-flex items-center justify-between rounded px-3 py-2 ",
          "leading-none h-10 gap-1 bg-surface-active text-white",
          "shadow-md hover:bg-surface-hover focus:outline-none focus:ring-2",
          "focus:ring-offset-2 focus:ring-offset-background w-full",
          "text-left",
          "disabled:cursor-not-allowed disabled:opacity-50",
          textSize
        )}
      >
        {value ? (
          <span
            className={
              "flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left"
            }
          >
            <SelectValue>{options.find((o) => o.value === value)?.label ?? value}</SelectValue>
          </span>
        ) : (
          <span className="text-white">{placeholder}</span>
        )}
        <SelectIcon>
          <ChevronDown className="text-white flex-shrink-0" />
        </SelectIcon>
      </SelectTriggerElement>
      <SelectPrimitive.Portal>
        <SelectContentElement
          className={cn(
            "overflow-hidden bg-surface rounded-md shadow-lg",
            "border border-surface-border",
            "z-50"
          )}
          position="popper"
          sideOffset={5}
          align="start"
          alignOffset={-4}
          avoidCollisions
          style={{ width: "var(--radix-select-trigger-width)" }}
        >
          <SelectScrollUpButtonElement className="flex items-center justify-center h-6 bg-surface text-muted cursor-default">
            <ChevronUp className={textSize} />
          </SelectScrollUpButtonElement>
          <SelectViewport className="p-1 max-h-60 overflow-y-auto">
            {(() => {
              const groups: { header?: string; items: SelectOption[] }[] = [];
              for (const option of options) {
                if (option.groupHeader || groups.length === 0) {
                  groups.push({ header: option.groupHeader, items: [option] });
                } else {
                  groups[groups.length - 1].items.push(option);
                }
              }
              return groups.map((group, groupIndex) => (
                <SelectGroup key={groupIndex}>
                  {group.header && (
                    <SelectLabel className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted">
                      {group.header}
                    </SelectLabel>
                  )}
                  {group.items.map((option) => (
                    <SelectItemElement
                      key={option.value}
                      value={option.value}
                      className={cn(
                        "leading-none text-white rounded flex items-center",
                        "pr-8 pl-6 py-3 relative select-none",
                        "data-[highlighted]:outline-none data-[highlighted]:bg-surface-active",
                        "data-[highlighted]:text-white cursor-pointer",
                        textSize,
                        option.emphasized && "font-semibold text-primary"
                      )}
                    >
                      <SelectItemText>{option.label}</SelectItemText>
                      <SelectItemIndicator className="absolute left-1 w-4 inline-flex items-center justify-center">
                        <Check />
                      </SelectItemIndicator>
                    </SelectItemElement>
                  ))}
                </SelectGroup>
              ));
            })()}
          </SelectViewport>
          <SelectScrollDownButtonElement className="flex items-center justify-center h-6 bg-surface text-muted cursor-default">
            <ChevronDown />
          </SelectScrollDownButtonElement>
        </SelectContentElement>
      </SelectPrimitive.Portal>
    </Select>
  );
};
export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
